/**
 * Test time and date utility functions
 * Centralized logic for parsing, formatting, and checking test schedules
 */

import { normalizeTestType } from './testCategories';

/**
 * Parse a scheduled time string to a Date object
 * @param {string} scheduledTimeStr - Scheduled time string (may include 'GMT')
 * @returns {Date} Parsed date object
 */
export const parseISTDate = (scheduledTimeStr) => {
  const cleanedStr = String(scheduledTimeStr || '').replace('GMT', '').trim();
  const parsed = new Date(cleanedStr);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
};

/**
 * Convert GMT date to IST formatted string
 * @param {string|Date} gmtDate - GMT date string or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const convertToIST = (gmtDate, options = {}) => {
  const date = new Date(gmtDate);
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
    ...options,
  };
  return isNaN(date.getTime()) ? "Invalid date" : date.toLocaleString('en-IN', defaultOptions);
};

/**
 * Check if user is a privileged user (bypasses expiration checks)
 * @returns {boolean} True if user is privileged
 */
const isPrivilegedUser = () => {
  const userEmail = localStorage.getItem("user_email");
  return userEmail === "anubhav.mishra@newzenalpha.com" || userEmail === "anubhav.mishra303@gmail.com";
};

/**
 * Check if a test has expired (more than 24 hours past scheduled time)
 * @param {string} scheduledTimeStr - Scheduled time string
 * @returns {boolean} True if test is expired
 */
export const isTestExpired = (scheduledTimeStr) => {
  if (isPrivilegedUser()) {
    return false;
  }
  const scheduledIST = parseISTDate(scheduledTimeStr);
  const now = new Date();
  const diffHours = (now - scheduledIST) / (1000 * 60 * 60);
  return diffHours > 24;
};

/**
 * Check if a test is available to start now
 * @param {string} scheduledTimeStr - Scheduled time string
 * @returns {boolean} True if test can be started now
 */
export const isTestAvailableNow = (scheduledTimeStr) => {
  const scheduledIST = parseISTDate(scheduledTimeStr);
  const now = new Date();
  if (isPrivilegedUser()) {
    return now >= scheduledIST;
  }
  return now >= scheduledIST && !isTestExpired(scheduledTimeStr);
};

/**
 * Check if a test should be visible (not expired)
 * @param {string} scheduledTimeStr - Scheduled time string
 * @returns {boolean} True if test should be visible
 */
export const isTestVisible = (scheduledTimeStr) => {
  if (isPrivilegedUser()) {
    return true;
  }
  const scheduledIST = parseISTDate(scheduledTimeStr);
  const now = new Date();
  const diffHours = (now - scheduledIST) / (1000 * 60 * 60);
  return diffHours <= 24;
};

/**
 * Get time remaining until test starts
 * @param {string} scheduledTimeStr - Scheduled time string
 * @param {boolean} includePrefix - Whether to include "in " prefix (default: false)
 * @returns {string|null} Formatted time remaining string or null if time has passed
 */
export const getTimeRemaining = (scheduledTimeStr, includePrefix = false) => {
  const scheduledIST = parseISTDate(scheduledTimeStr);
  const now = new Date();
  const diffMs = scheduledIST - now;
  if (diffMs <= 0) return null;
  
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  const seconds = diffSeconds % 60;
  
  const prefix = includePrefix ? "in " : "";
  
  if (hours > 0) {
    return `${prefix}${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${prefix}${minutes}m`;
  }
  return `${prefix}${seconds}s`;
};

/**
 * Get test type label for display
 * @param {string} type - Test type code
 * @returns {string} Human-readable test type label
 */
export const getTestTypeLabel = (type) => {
  const base = normalizeTestType(type);
  const typeMap = {
    'c': "Coding",
    't': "Screening",
    'p': "Personality",
    'b': "Behavioral",
    'o': "Communication",
    'a': "P&B",
    'rs': "RT Screening",
    'rpnb': "RT P&B",
    'ro': "RT Communication",
    'fsc': "Full Stack Coding",
    'fspb': "Full Stack P&B",
    'fscp': "Full Stack",
    'fspc': "Full Stack",
    'fspc20': "Full Stack",
  };
  return typeMap[base] || "Test";
};
